import { AfterViewInit, Component, Input, Renderer2 } from '@angular/core';
import { DsButtonVariants, DsTooltipPosition } from '@levelaccess/design-system';
import { TranslateService } from '../../../../../translate/translate.service';
import { BaseCellComponent, ICellConfig } from '../base-cell/base-cell.component';

export interface IDSTooltipItemsCellConfig extends ICellConfig {
  items: string[];
  itemDisplaySeparator?: string;
  maxVisibleItems?: number;
  additionalItemsTranslationKey: string;
  tooltipPlacement?: DsTooltipPosition;
}

@Component({
  selector: 'table-cell-ds-tooltip-items',
  standalone: false,
  styleUrls: ['../base-cell/base-cell.component.scss'],
  template: `
    <span #ref>{{ displayText }}</span>
    @if (hasAdditionalItems) {
      <button ds-button [variant]="DsButtonVariants.microActionStacked" [ds-tooltip]="tooltipText" [hideMicroActionIcon]="true">
        {{ additionalItemsText }}
      </button>
    }
  `,
})
export class DSTooltipItemsCellComponent extends BaseCellComponent implements AfterViewInit {
  @Input() config: IDSTooltipItemsCellConfig;

  protected DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;

  constructor(
    protected renderer: Renderer2,
    private readonly translateService: TranslateService,
  ) {
    super(renderer);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();

    if (this.config?.styles && this.element?.nativeElement) {
      for (const [key, value] of Object.entries(this.config.styles)) {
        this.renderer.setStyle(this.element.nativeElement, key, value);
      }
    }
  }

  get maxItemsToShow(): number {
    return this.config?.maxVisibleItems ?? 2;
  }

  get visibleItems(): string[] {
    return this.config.items.slice(0, this.maxItemsToShow);
  }

  get hasAdditionalItems(): boolean {
    return this.config.items.length > this.maxItemsToShow;
  }

  get additionalItemsCount(): number {
    return this.config.items.length - this.maxItemsToShow;
  }

  get displayText(): string {
    const itemsText = this.visibleItems.map((item: string) => item).join(this.config?.itemDisplaySeparator ?? '\n');
    return this.hasAdditionalItems ? itemsText + '\n' : itemsText;
  }

  get additionalItemsText(): string {
    return this.translateService.instant(this.config.additionalItemsTranslationKey, this.additionalItemsCount);
  }

  get tooltipText(): string {
    return this.config.items
      .slice(this.maxItemsToShow)
      .map((item: string) => item)
      .join(this.config?.itemDisplaySeparator ?? '\n');
  }

  get tooltipPlacement(): DsTooltipPosition {
    return this.config?.tooltipPlacement ?? DsTooltipPosition.top;
  }
}
